import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Reveal } from "@/components/Section";
import { HeroBanner } from "@/components/Section";
import { getProducts } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

export default function Products() {
  const { content } = useContent();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("All");
  const [brand, setBrand] = useState("All");
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    getProducts().then(setProducts);
  }, []);

  const CATEGORIES = useMemo(() => {
    const fromSettings = content.product_categories || [];
    const fromProducts = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
    const merged = Array.from(new Set([...fromSettings, ...fromProducts]));
    return ["All", ...merged];
  }, [content.product_categories, products]);

  useEffect(() => {
    const focus = searchParams.get("focus");
    if (focus && products.length) {
      const p = products.find((x) => x.id === focus);
      if (p) setSelected(p);
    }
  }, [searchParams, products]);

  const brands = useMemo(
    () => ["All", ...Array.from(new Set(products.map((p) => p.brand)))],
    [products]
  );

  const filtered = products.filter(
    (p) => (active === "All" || p.category === active) && (brand === "All" || p.brand === brand)
  );

  const closeDialog = () => {
    setSelected(null);
    if (searchParams.get("focus")) {
      searchParams.delete("focus");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div data-testid="products-page">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-border carbon-texture">
        <HeroBanner src={content.page_banners?.products} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary">
            {content.page_headers?.products_eyebrow || "Our Catalogue"}
          </span>
          <h1 className="mt-4 font-heading text-5xl font-bold tracking-tight text-white md:text-6xl">
            {content.page_headers?.products_title || "Products"}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">
            {content.page_headers?.products_description || "Explore our complete range of genuine Eurogrip motorcycle tyres and premium Bajaj & ENOC lubricants for two-wheelers and three-wheelers — every product distributed with an uncompromising commitment to authenticity, performance and value."}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[101px] z-30 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-category-${c.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`px-4 py-2 font-heading text-xs font-bold tracking-wide transition-colors ${
                  active === c
                    ? "bg-primary text-white"
                    : "border border-border text-muted-foreground hover:border-primary hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => (
              <button
                key={b}
                data-testid={`filter-brand-${b.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setBrand(b)}
                className={`px-3 py-2 text-xs font-medium tracking-wide transition-colors ${
                  brand === b ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <p className="mb-8 text-sm text-muted-foreground" data-testid="products-count">
          Showing <span className="font-bold text-foreground">{filtered.length}</span> products
        </p>
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.05}>
              <button
                data-testid={`product-card-${p.id}`}
                onClick={() => setSelected(p)}
                className="group flex h-full w-full flex-col border border-border bg-card text-left transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-xl"
              >
                <div className="relative aspect-square overflow-hidden bg-white">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-0 top-0 bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                    {p.category}
                  </span>
                  {p.highlight && (
                    <span className="absolute right-0 top-0 bg-foreground px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-background">
                      {p.highlight}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col border-t border-border p-5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                    {p.brand}
                  </span>
                  <h3 className="mt-1 font-heading text-base font-semibold leading-tight tracking-tight text-foreground">
                    {p.name}
                  </h3>
                  <span className="mt-auto pt-3 text-xs font-medium tracking-wide text-muted-foreground">
                    {p.position} · Details →
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="py-20 text-center text-muted-foreground">No products match your filter.</p>
        )}
      </section>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent
          className="max-w-3xl gap-0 overflow-hidden rounded-xl border-2 border-border p-0"
          data-testid="product-detail-dialog"
        >
          {selected && (
            <div className="grid md:grid-cols-2">
              <div className="flex items-center justify-center bg-white p-8">
                <img
                  src={selected.image}
                  alt={selected.name}
                  className="max-h-72 w-full object-contain"
                />
              </div>
              <div className="p-8">
                <DialogHeader>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                    {selected.brand} · {selected.category}
                  </span>
                  <DialogTitle className="mt-2 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground">
                    {selected.name}
                  </DialogTitle>
                </DialogHeader>
                {selected.description && (
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-testid="product-detail-description">
                    {selected.description}
                  </p>
                )}
                <dl className="mt-6 divide-y divide-border border-y border-border">
                  {Object.entries(selected.specs || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 py-2.5">
                      <dt className="text-xs font-bold tracking-wide text-muted-foreground">
                        {k}
                      </dt>
                      <dd className="text-right text-sm font-medium text-foreground">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-muted p-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">
                    Fits
                  </span>
                  <span className="text-xs leading-relaxed text-muted-foreground">
                    Compatible with popular {selected.position || "2W / 3W"} motorcycle & scooter
                    models. Contact us to confirm the right fit for your vehicle.
                  </span>
                </div>
                <Link
                  to="/support"
                  data-testid="product-inquire-btn"
                  className="mt-6 flex w-full items-center justify-center gap-2 bg-primary px-6 py-3 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:bg-primary/90 shadow-md hover:shadow-lg"
                >
                  Inquire About This <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
